import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';

const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID
};

const userPool = new CognitoUserPool(poolData);

// Tokens em sessionStorage (escopo de aba, não persiste entre sessões)
// Dados não-sensíveis de UI (nome, role) ficam em localStorage
const tokenStore = {
  set(idToken, accessToken, refreshToken) {
    sessionStorage.setItem('idToken', idToken);
    sessionStorage.setItem('accessToken', accessToken);
    sessionStorage.setItem('refreshToken', refreshToken);
  },
  getIdToken() {
    return sessionStorage.getItem('idToken');
  },
  clear() {
    sessionStorage.removeItem('idToken');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    // limpar chaves legadas que possam existir no localStorage
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('tokens');
  }
};

export const cognitoAuth = {
  async signIn(email, password) {
    return new Promise((resolve) => {
      const authDetails = new AuthenticationDetails({ Username: email, Password: password });
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (result) => {
          const idToken = result.getIdToken();
          const payload = idToken.payload;

          tokenStore.set(
            idToken.getJwtToken(),
            result.getAccessToken().getJwtToken(),
            result.getRefreshToken().getToken()
          );

          resolve({
            success: true,
            user: {
              email: payload.email,
              name: payload.name || email.split('@')[0],
              role: payload['custom:role'] || 'user',
              tenantId: payload['custom:tenant_id'] || null
            }
          });
        },

        onFailure: (err) => {
          const messages = {
            UserNotConfirmedException:      'Usuário não confirmado',
            PasswordResetRequiredException: 'Necessário redefinir senha',
            NotAuthorizedException:         'Email ou senha inválidos',
            UserNotFoundException:          'Usuário não encontrado',
          };
          resolve({ success: false, error: messages[err.code] || 'Email ou senha inválidos' });
        },

        newPasswordRequired: () => {
          resolve({ success: false, error: 'Necessário trocar senha no primeiro acesso' });
        }
      });
    });
  },

  async forgotPassword(email) {
    return new Promise((resolve) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.forgotPassword({
        onSuccess: () => resolve({ success: true }),
        onFailure: (err) => {
          // Mapeia os erros comuns do Cognito para mensagens úteis. Mantém o code
          // original em `code` para diagnóstico/suporte.
          const messages = {
            // Sem canal de recuperação válido (email não verificado e sem telefone).
            InvalidParameterException:
              'Não foi possível enviar o email de recuperação: a conta não tem um email verificado. Entre em contato com o suporte.',
            // Excesso de tentativas.
            LimitExceededException:
              'Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.',
            // Não existe / desabilitado — resposta genérica para não vazar existência de conta.
            UserNotFoundException:
              'Se este email estiver cadastrado, você receberá as instruções de recuperação.',
            NotAuthorizedException:
              'Não foi possível recuperar a senha desta conta. Entre em contato com o suporte.',
            CodeDeliveryFailureException:
              'Falha ao entregar o email de recuperação. Verifique o endereço ou tente mais tarde.',
          };
          resolve({
            success: false,
            code: err?.code || null,
            error: messages[err?.code] || 'Erro ao enviar email de recuperação. Tente novamente ou contate o suporte.',
          });
        },
      });
    });
  },

  async confirmPassword(email, code, newPassword) {
    return new Promise((resolve) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => resolve({ success: true }),
        onFailure: (err) => resolve({ success: false, error: err.message || 'Código inválido ou expirado' })
      });
    });
  },

  async confirmSignUp(email, code) {
    return new Promise((resolve) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.confirmRegistration(code, true, (err) => {
        if (err) return resolve({ success: false, error: err.message || 'Código inválido ou expirado' });
        resolve({ success: true });
      });
    });
  },

  async resendConfirmationCode(email) {
    return new Promise((resolve) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      cognitoUser.resendConfirmationCode((err) => {
        if (err) return resolve({ success: false, error: err.message });
        resolve({ success: true });
      });
    });
  },

  async changePassword(oldPassword, newPassword) {
    return new Promise((resolve) => {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) return resolve({ success: false, error: 'Sessão expirada' });
      cognitoUser.getSession((err) => {
        if (err) return resolve({ success: false, error: 'Sessão expirada' });
        cognitoUser.changePassword(oldPassword, newPassword, (err2) => {
          if (err2) return resolve({ success: false, error: err2.message || 'Senha atual incorreta' });
          resolve({ success: true });
        });
      });
    });
  },

  signOut() {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) cognitoUser.signOut();
    tokenStore.clear();
    localStorage.removeItem('user');
  },

  getIdToken: () => tokenStore.getIdToken(),

  // ── Google OAuth via Cognito Hosted UI ─────────────────────────────────────
  getGoogleLoginUrl() {
    const domain   = process.env.REACT_APP_COGNITO_HOSTED_UI_DOMAIN;
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    const redirect = `${process.env.REACT_APP_APP_URL}/auth/callback`;
    if (!domain || !clientId) return null;
    const params = new URLSearchParams({
      response_type:     'code',
      client_id:         clientId,
      redirect_uri:      redirect,
      identity_provider: 'Google',
      scope:             'openid email profile'
    });
    return `${domain}/oauth2/authorize?${params}`;
  },

  async exchangeCodeForTokens(code) {
    const domain   = process.env.REACT_APP_COGNITO_HOSTED_UI_DOMAIN;
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    const redirect = `${process.env.REACT_APP_APP_URL}/auth/callback`;

    const res = await fetch(`${domain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type:   'authorization_code',
        client_id:    clientId,
        redirect_uri: redirect,
        code
      })
    });
    if (!res.ok) throw new Error('Token exchange failed');
    const data = await res.json();

    // Decodifica o ID token (JWT) sem verificar assinatura — Cognito já validou
    const payload = JSON.parse(atob(data.id_token.split('.')[1]));

    tokenStore.set(data.id_token, data.access_token, data.refresh_token);

    return {
      idToken:     data.id_token,
      accessToken: data.access_token,
      user: {
        email:    payload.email,
        name:     payload.name || payload.email.split('@')[0],
        role:     payload['custom:role']     || null,
        tenantId: payload['custom:tenant_id'] || null
      }
    };
  },
};
